import { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  FirstAidKit, Plus, Wrench, ClipboardText, User, Warning, CheckCircle, X,
  MagnifyingGlass, Clock, ArrowSquareOut, Prohibit, Package, Trash, PencilSimple,
  ArrowRight, SpeakerHigh, SpeakerSlash,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import AccessoryChips from "@/components/AccessoryChips";
import TicketIssueBlock from "@/components/hospital/TicketIssueBlock";
import TicketCard from "@/components/hospital/TicketCard";
import ReadyStrip from "@/components/hospital/ReadyStrip";
import Fold from "@/components/Fold";

const PRIORITY_META = {
  urgent: { label: "URGENTE", color: "#E0000F", bg: "#fee2e2" },
  high:   { label: "ALTA",    color: "#d97706", bg: "#fef3c7" },
  normal: { label: "NORMAL",  color: "#1E88E5", bg: "#dbeafe" },
  low:    { label: "BAJA",    color: "#52525B", bg: "#f4f4f5" },
};

const STATUS_META = {
  pendiente_diagnostico:  { label: "Pendiente diagnóstico", color: "#E0000F", bg: "#fee2e2" },
  en_diagnostico:         { label: "En diagnóstico",        color: "#d97706", bg: "#fef3c7" },
  pendiente_refaccion:    { label: "Pendiente refacción",   color: "#7E57C2", bg: "#ede9fe" },
  en_espera_autorizacion: { label: "Espera autorización",   color: "#1E88E5", bg: "#dbeafe" },
  enviado_externo:        { label: "Con técnico externo",   color: "#0891b2", bg: "#cffafe" },
  listo:                  { label: "Listo · alta",           color: "#00873D", bg: "#dcfce7" },
  irreparable:            { label: "Irreparable",           color: "#52525B", bg: "#e4e4e7" },
  baja_definitiva:        { label: "Baja definitiva",        color: "#000",    bg: "#e4e4e7" },
};

const KANBAN_COLUMNS = [
  { key: "pendiente_diagnostico", title: "Pendiente diagnóstico" },
  { key: "en_diagnostico",        title: "En diagnóstico" },
  { key: "pendiente_refaccion",   title: "Pendiente refacción" },
  { key: "enviado_externo",       title: "Técnico externo" },
];

// Estados a los que el técnico puede mover un ticket desde el modal
// (excluye estados terminales que requieren el flujo de "Entregar/Alta").
const MODAL_STATE_OPTIONS = [
  "pendiente_diagnostico",
  "en_diagnostico",
  "pendiente_refaccion",
  "enviado_externo",
];

const EMPTY_NEW = {
  product_id: "", serial_number: "", reason: "", fault_description: "",
  priority: "normal", requesting_area: "Ventas", comments: "", location: "", qty: 1,
};

function daysBetween(iso) {
  if (!iso) return 0;
  return Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
}

function fmtCurrency(n) {
  return `MX$${Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;
}

export default function Hospital() {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showNew, setShowNew] = useState(false);
  const [newForm, setNewForm] = useState(EMPTY_NEW);
  const [productSearch, setProductSearch] = useState("");
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState(null);        // ticket viewing details
  const [dischargeFor, setDischargeFor] = useState(null);
  const [dischargeForm, setDischargeForm] = useState({
    ready_for_sale: true, reason_not_ready: "pendiente_refaccion",
    diagnosis: "", actions_taken: "", minutes_invested: 0, repair_cost: 0, parts_used: "",
  });
  const [cancelId, setCancelId] = useState(null);
  const [overCol, setOverCol] = useState(null);
  const [alertMuted, setAlertMuted] = useState(() => {
    try { return localStorage.getItem("hospital_alert_muted") === "1"; } catch { return false; }
  });
  const knownTicketIdsRef = useRef(null); // Set<string>; null == pre-first-load
  const audioCtxRef = useRef(null);
  const dragRef = useRef(null);

  // Sonido de alerta al llegar tickets nuevos de PISO DE VENTA o CLIENTE.
  // Usa Web Audio API para no depender de un archivo mp3 hospedado. Dos
  // tonos rápidos (880Hz → 1320Hz) que se distinguen del ruido del navegador.
  const playPriorityBeep = () => {
    if (alertMuted) return;
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        audioCtxRef.current = new Ctx();
      }
      const ctx = audioCtxRef.current;
      if (ctx.state === "suspended") ctx.resume().catch(() => {});
      const now = ctx.currentTime;
      [
        { f: 880,  t: now,        d: 0.18 },
        { f: 1320, t: now + 0.22, d: 0.28 },
      ].forEach(({ f, t, d }) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.type = "sine";
        osc.frequency.setValueAtTime(f, t);
        gain.gain.setValueAtTime(0.0001, t);
        gain.gain.exponentialRampToValueAtTime(0.35, t + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, t + d);
        osc.connect(gain).connect(ctx.destination);
        osc.start(t);
        osc.stop(t + d + 0.05);
      });
    } catch { /* silent fallback */ }
  };

  const toggleMute = () => {
    setAlertMuted((prev) => {
      const next = !prev;
      try { localStorage.setItem("hospital_alert_muted", next ? "1" : "0"); } catch { /* ignore */ }
      // Un beep de confirmación al reactivar para probar el audio.
      if (!next) {
        setTimeout(() => {
          try {
            if (!audioCtxRef.current) {
              const Ctx = window.AudioContext || window.webkitAudioContext;
              audioCtxRef.current = new Ctx();
            }
            const ctx = audioCtxRef.current;
            if (ctx.state === "suspended") ctx.resume().catch(() => {});
            const osc = ctx.createOscillator();
            const gain = ctx.createGain();
            osc.type = "sine";
            osc.frequency.setValueAtTime(1320, ctx.currentTime);
            gain.gain.setValueAtTime(0.0001, ctx.currentTime);
            gain.gain.exponentialRampToValueAtTime(0.3, ctx.currentTime + 0.02);
            gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.2);
            osc.connect(gain).connect(ctx.destination);
            osc.start(ctx.currentTime);
            osc.stop(ctx.currentTime + 0.25);
          } catch { /* ignore */ }
        }, 40);
      }
      return next;
    });
  };

  const load = async () => {
    const [t, s, p, e] = await Promise.all([
      api.get("/hospital/tickets"),
      api.get("/hospital/stats"),
      api.get("/products").catch(() => ({ data: [] })),
      api.get("/hr/employees", { params: { status: "active" } }).catch(() => ({ data: [] })),
    ]);
    const nextTickets = t.data || [];
    // Detección de tickets nuevos de PISO DE VENTA / CLIENTE para disparar el
    // beep. En el primer load no hay comparación: solo poblamos el set.
    const prev = knownTicketIdsRef.current;
    const nextIds = new Set(nextTickets.map((x) => x.id));
    if (prev !== null) {
      const priorityArrivals = nextTickets.filter(
        (x) => !prev.has(x.id) && (x.source === "piso_de_venta" || x.source === "cliente"),
      );
      if (priorityArrivals.length > 0) {
        playPriorityBeep();
        const first = priorityArrivals[0];
        const label = first.source === "cliente" ? "Máquina de cliente" : "Piso de venta";
        toast(`Nuevo ingreso · ${label}`, {
          description: `${first.product_name}${priorityArrivals.length > 1 ? ` y ${priorityArrivals.length - 1} más` : ""}`,
          duration: 6000,
        });
      }
    }
    knownTicketIdsRef.current = nextIds;
    setTickets(nextTickets);
    setStats(s.data);
    setProducts(p.data || []);
    setEmployees(e.data || []);
  };

  useEffect(() => {
    load();
    // Refresh cada 15s para que el board se actualice cuando otro técnico
    // tome una tarjeta o cambie el estado.
    const int = setInterval(load, 15000);
    return () => clearInterval(int);
    // eslint-disable-next-line
  }, []);

  // Deep-link: si venimos de "Mi Tablero" con ?ticket=<id>, abrir detalle.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const tid = searchParams.get("ticket");
    if (!tid) return;
    (async () => {
      try {
        const { data } = await api.get(`/hospital/tickets/${tid}`);
        setDetail(data);
      } catch { /* ignore */ }
      // Limpia el query param para no re-abrir en refreshes.
      const next = new URLSearchParams(searchParams);
      next.delete("ticket");
      setSearchParams(next, { replace: true });
    })();
    // eslint-disable-next-line
  }, []);

  const filteredTickets = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return tickets;
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    const tokens = norm(q).split(/\s+/).filter(Boolean);
    return tickets.filter((t) => {
      const hay = `${norm(t.product_name)} ${norm(t.product_sku)} ${norm(t.serial_number)} ${norm(t.reason)} ${norm(t.fault_description)} ${norm(t.technician_name)} ${norm(t.requesting_area)}`;
      return tokens.every((tok) => hay.includes(tok));
    });
  }, [tickets, searchQuery]);

  const columns = useMemo(() => {
    const byStatus = {};
    for (const col of KANBAN_COLUMNS) byStatus[col.key] = [];
    for (const t of filteredTickets) {
      if (byStatus[t.status]) byStatus[t.status].push(t);
    }
    return byStatus;
  }, [filteredTickets]);
  const readyTickets = useMemo(() => filteredTickets.filter((t) => t.status === "listo"), [filteredTickets]);

  const filteredProducts = useMemo(() => {
    const q = productSearch.trim().toLowerCase();
    const norm = (s) => (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
    if (!q) return products.slice(0, 50);
    const tokens = norm(q).split(/\s+/).filter(Boolean);
    return products.filter((p) => {
      const hay = `${norm(p.name)} ${norm(p.sku)} ${norm(p.brand)} ${norm(p.category)} ${norm(p.characteristics)} ${norm(p.model)}`;
      return tokens.every((t) => hay.includes(t));
    }).slice(0, 50);
  }, [products, productSearch]);

  const submitNew = async (e) => {
    e.preventDefault();
    if (!newForm.product_id) return toast.error("Selecciona un producto");
    if (!newForm.fault_description.trim()) return toast.error("Describe la falla");
    setSaving(true);
    try {
      // El backend requiere `reason`; lo derivamos de la descripción de la falla.
      const payload = { ...newForm, reason: newForm.fault_description.trim() };
      await api.post("/hospital/tickets", payload);
      toast.success("Equipo enviado al taller · stock bloqueado");
      setShowNew(false);
      setNewForm(EMPTY_NEW);
      setProductSearch("");
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  // Drag-and-drop entre columnas del Kanban. Cada transición tiene su propia
  // API (no hay un "set status" genérico), así que abrimos el diálogo correcto
  // o llamamos al endpoint correspondiente.
  const handleDropOn = async (toCol) => {
    const tid = dragRef.current;
    dragRef.current = null;
    setOverCol(null);
    if (!tid) return;
    const ticket = tickets.find((t) => t.id === tid);
    if (!ticket || ticket.status === toCol) return;
    // Dar de alta ("Listo · alta") es el único estado terminal — sí abre
    // el diálogo de entrega/alta.
    if (toCol === "listo") {
      setDischargeFor(ticket);
      setDischargeForm({
        ready_for_sale: true,
        reason_not_ready: "pendiente_refaccion",
        diagnosis: ticket.diagnosis || "", actions_taken: ticket.actions_taken || "",
        minutes_invested: ticket.minutes_invested || 0,
        repair_cost: ticket.repair_cost || 0, parts_used: ticket.parts_used || "",
      });
      return;
    }
    // Salto directo Recepción → En diagnóstico usa el endpoint /start (para
    // marcar started_at + auto-claim). El resto son movimientos silenciosos
    // entre columnas intermedias.
    if (ticket.status === "pendiente_diagnostico" && toCol === "en_diagnostico") {
      try {
        await api.post(`/hospital/tickets/${tid}/start`);
        toast.success("Diagnóstico iniciado");
        load();
      } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
      return;
    }
    try {
      await api.patch(`/hospital/tickets/${tid}`, { status: toCol });
      toast.success("Ticket movido");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Transición no permitida");
    }
  };

  const startDiagnosis = async (t, technicianId) => {
    try {
      await api.post(`/hospital/tickets/${t.id}/start`, null, { params: technicianId ? { technician_id: technicianId } : {} });
      toast.success("Diagnóstico iniciado");
      load();
      if (detail?.id === t.id) {
        const upd = await api.get(`/hospital/tickets/${t.id}`);
        setDetail(upd.data);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  // Self-service: el técnico logueado toma la tarjeta y la mueve a en_diagnostico.
  const claimTicket = async (t, e) => {
    e?.stopPropagation?.();
    try {
      await api.post(`/hospital/tickets/${t.id}/claim`);
      toast.success("Tomaste la tarjeta · en diagnóstico");
      if (detail?.id === t.id) {
        const { data } = await api.get(`/hospital/tickets/${t.id}`);
        setDetail(data);
      }
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo tomar");
    }
  };

  const assignTech = async (tid, technicianId) => {
    try {
      await api.patch(`/hospital/tickets/${tid}`, { technician_id: technicianId });
      toast.success("Técnico asignado");
      load();
      if (detail?.id === tid) {
        const upd = await api.get(`/hospital/tickets/${tid}`);
        setDetail(upd.data);
      }
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  const openDischarge = (t) => {
    setDischargeFor(t);
    setDischargeForm({
      ready_for_sale: true,
      reason_not_ready: "pendiente_refaccion",
      diagnosis: t.diagnosis || "",
      actions_taken: t.actions_taken || "",
      minutes_invested: t.minutes_invested || 0,
      repair_cost: t.repair_cost || 0,
      parts_used: t.parts_used || "",
    });
  };

  const submitDischarge = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post(`/hospital/tickets/${dischargeFor.id}/discharge`, dischargeForm);
      const isClient = dischargeFor?.source === "cliente";
      toast.success(
        isClient
          ? "Ticket cerrado · equipo listo para entregar al cliente"
          : "Equipo dado de alta · Listo"
      );
      setDischargeFor(null);
      setDetail(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
    finally { setSaving(false); }
  };

  const confirmCancel = async () => {
    if (!cancelId) return;
    try {
      await api.delete(`/hospital/tickets/${cancelId}`);
      toast.success("Ticket cancelado · unidad liberada");
      setCancelId(null);
      setDetail(null);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Error"); }
  };

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="hospital-page">
      <header className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="font-mono-data text-xs tracking-widest text-zinc-500 mb-1 flex items-center gap-1.5">
            <FirstAidKit size={12} weight="fill"/> TALLER · REPARACIONES
          </div>
          <h1 className="font-heading text-4xl font-extrabold tracking-tight">Taller y Reparación</h1>
          <p className="text-zinc-500 mt-1 text-sm">Cada equipo defectuoso es un paciente: ingresa, se diagnostica y sale de alta. Mientras esté aquí, no puede venderse.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            type="button"
            onClick={toggleMute}
            title={alertMuted ? "Alertas silenciadas · click para activar" : "Alertas activas · click para silenciar"}
            className={`inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-widest px-3 py-2 border rounded-sm transition-colors ${alertMuted ? "border-zinc-200 text-zinc-400 bg-zinc-50 hover:border-zinc-300" : "border-[#00873D] text-[#00873D] bg-emerald-50 hover:bg-emerald-100"}`}
            data-testid="hospital-alert-toggle"
          >
            {alertMuted ? <SpeakerSlash size={13} weight="bold" /> : <SpeakerHigh size={13} weight="bold" />}
            {alertMuted ? "Alertas OFF" : "Alertas ON"}
          </button>
          <Button onClick={() => setShowNew(true)} className="bg-[#E0000F] hover:bg-[#b8000c] text-white gap-2" data-testid="new-ticket-btn">
            <Plus size={16} weight="bold"/> Enviar equipo al taller
          </Button>
        </div>
      </header>

      {/* Stats · compactas */}
      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="hospital-stats">
          {[
            { label: "Activos", value: stats.active_count, cls: "text-[#002FA7]", testid: "stat-active" },
            { label: "Pendientes", value: stats.pending_count, cls: "text-[#E0000F]", testid: "stat-pending" },
            { label: "En diagnóstico", value: stats.in_diagnosis_count, cls: "text-amber-600", testid: "stat-indiag" },
            { label: "Listos", value: stats.ready_count, cls: "text-[#00873D]", testid: "stat-ready" },
            { label: "Tiempo prom.", value: `${Math.round(stats.avg_repair_minutes / 60 * 10) / 10}h`, cls: "text-zinc-700", testid: "stat-avg" },
          ].map((s) => (
            <div key={s.label} className="rounded-md border border-zinc-200 bg-white px-3 py-2 flex items-baseline justify-between gap-2">
              <span className="text-[10px] uppercase tracking-widest text-zinc-500">{s.label}</span>
              <span className={`font-heading text-xl font-extrabold numeric ${s.cls}`} data-testid={s.testid}>{s.value}</span>
            </div>
          ))}
        </div>
      )}

      {/* Search */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold"/>
          <Input
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar equipo, serie, técnico o falla…"
            className="pl-9 pr-8"
            data-testid="hospital-search"
          />
          {searchQuery && (
            <button type="button" onClick={() => setSearchQuery("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-400 hover:text-zinc-600">
              <X size={14} weight="bold"/>
            </button>
          )}
        </div>
        <span className="text-[11px] text-zinc-400">Toca una tarjeta para ver el detalle · arrástrala para cambiar de etapa</span>
      </div>

      {/* Listos · alta — zona propia y de drop */}
      <ReadyStrip
        tickets={readyTickets}
        onOpen={setDetail}
        isOver={overCol === "listo"}
        dropHandlers={{
          onDragEnter: () => setOverCol("listo"),
          onDragOver: (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; },
          onDragLeave: () => setOverCol((v) => (v === "listo" ? null : v)),
          onDrop: (e) => { e.preventDefault(); handleDropOn("listo"); },
        }}
      />

      {/* Kanban · 4 etapas de trabajo */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="hospital-kanban">
        {KANBAN_COLUMNS.map((col) => {
          const items = columns[col.key] || [];
          const stMeta = STATUS_META[col.key];
          return (
            <div
              key={col.key}
              onDragEnter={() => setOverCol(col.key)}
              onDragOver={(e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; }}
              onDragLeave={() => setOverCol((v) => (v === col.key ? null : v))}
              onDrop={(e) => { e.preventDefault(); handleDropOn(col.key); }}
              className={`rounded-lg border transition-colors ${overCol === col.key ? "border-[#002FA7] border-dashed bg-[#002FA7]/5" : "border-zinc-200 bg-zinc-50/60"}`}
              data-testid={`col-${col.key}`}
            >
              <div className="px-3 py-2.5 flex items-center justify-between">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="w-2 h-2 rounded-full shrink-0" style={{ background: stMeta.color }}/>
                  <span className="text-xs font-heading font-bold uppercase tracking-wider truncate">{col.title}</span>
                </div>
                <span className="text-xs font-mono-data text-zinc-500 bg-white border border-zinc-200 rounded-full px-1.5" data-testid={`col-count-${col.key}`}>{items.length}</span>
              </div>
              <div className="px-2 pb-2 space-y-2 min-h-[220px] max-h-[70vh] overflow-y-auto">
                {items.length === 0 && (
                  <div className="text-center text-[11px] text-zinc-400 py-10 italic">Sin equipos</div>
                )}
                {items.map((t) => (
                  <TicketCard key={t.id} ticket={t} onOpen={setDetail} dragRef={dragRef} onDragEnd={() => { dragRef.current = null; setOverCol(null); }} />
                ))}
              </div>
            </div>
          );
        })}
      </div>


      {/* NEW TICKET DIALOG */}
      <Dialog open={showNew} onOpenChange={(o) => { if (!o) { setShowNew(false); setNewForm(EMPTY_NEW); setProductSearch(""); } }}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <FirstAidKit size={20} weight="fill" className="text-[#E0000F]"/> Enviar equipo al taller
            </DialogTitle>
            <DialogDescription>Registra la falla. El equipo se marcará automáticamente como <b>en taller (hold)</b> y no podrá venderse hasta darse de alta.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitNew} className="space-y-4">
            {/* Product picker */}
            <div>
              <Label className="text-xs uppercase tracking-wider">Producto *</Label>
              {newForm.product_id ? (() => {
                const p = products.find((x) => x.id === newForm.product_id);
                if (!p) return null;
                return (
                  <div className="mt-1 border border-zinc-300 bg-zinc-50 p-3 flex items-center justify-between">
                    <div>
                      <div className="font-semibold text-zinc-800">{p.name}</div>
                      <div className="text-xs text-zinc-500 mt-0.5 font-mono-data">
                        SKU {p.sku || "—"} · Stock {p.stock} · {p.hospital_qty || 0} en taller
                      </div>
                    </div>
                    <button type="button" onClick={() => setNewForm({ ...newForm, product_id: "" })} className="text-xs text-[#E0000F] hover:underline">Cambiar</button>
                  </div>
                );
              })() : (
                <>
                  <div className="relative mt-1">
                    <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" weight="bold"/>
                    <Input
                      value={productSearch}
                      onChange={(e) => setProductSearch(e.target.value)}
                      placeholder="Busca por nombre, SKU o marca..."
                      className="pl-9"
                      data-testid="product-search"
                    />
                  </div>
                  <div className="mt-2 max-h-72 overflow-y-auto border border-zinc-100 divide-y divide-zinc-100">
                    {filteredProducts.length === 0 && <div className="p-3 text-center text-xs text-zinc-400">Sin resultados</div>}
                    {filteredProducts.map((p, idx) => {
                      const avail = (p.stock || 0) - (p.hospital_qty || 0);
                      const noStock = avail <= 0;
                      return (
                        <button
                          key={`${p.id || "p"}-${idx}`}
                          type="button"
                          onClick={() => setNewForm({ ...newForm, product_id: p.id })}
                          className="w-full text-left p-2 hover:bg-zinc-50 flex items-center justify-between gap-2"
                          data-testid={`product-opt-${p.id}`}
                        >
                          <div className="min-w-0">
                            <div className="font-medium text-sm text-zinc-800 truncate">{p.name}</div>
                            <div className="text-[10px] text-zinc-500 font-mono-data truncate">
                              SKU {p.sku || "—"} · {p.brand || "—"}{p.category ? ` · ${p.category}` : ""}
                            </div>
                          </div>
                          <div className={`text-[10px] font-mono-data whitespace-nowrap ${noStock ? "text-[#E0000F]" : "text-zinc-500"}`}>
                            Stock {p.stock || 0}{p.hospital_qty ? ` (${p.hospital_qty} en taller)` : ""}
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </>
              )}
            </div>

            <div>
              <Label className="text-xs uppercase tracking-wider">Descripción de la falla *</Label>
              <Textarea required value={newForm.fault_description} onChange={(e) => setNewForm({ ...newForm, fault_description: e.target.value })} rows={4} placeholder="No carga batería, pantalla defectuosa, teclado falla, errores de sistema..." className="mt-1" data-testid="new-fault"/>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-wider">Ubicación física</Label>
              <Textarea value={newForm.location} onChange={(e) => setNewForm({ ...newForm, location: e.target.value })} rows={4} placeholder="Mesa 2, Estante B, planta baja, área de recepción..." className="mt-1" data-testid="new-location"/>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowNew(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving || !newForm.product_id} className="bg-[#E0000F] hover:bg-[#b8000c] text-white" data-testid="new-submit">
                {saving ? "Enviando..." : "Enviar al taller"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* TICKET DETAIL */}
      <Dialog open={!!detail} onOpenChange={(o) => { if (!o) setDetail(null); }}>
        <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto">
          {detail && (() => {
            const stMeta = STATUS_META[detail.status];
            const prMeta = PRIORITY_META[detail.priority] || PRIORITY_META.normal;
            const days = daysBetween(detail.created_at);
            const canWork = ["pendiente_diagnostico", "en_diagnostico"].includes(detail.status);
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-heading text-xl flex items-center gap-3 flex-wrap">
                    <FirstAidKit size={20} weight="fill" className="text-[#E0000F]"/>
                    <span>{detail.product_name}</span>
                    <span className="text-[10px] font-mono-data px-2 py-0.5 rounded" style={{ background: prMeta.bg, color: prMeta.color }}>{prMeta.label}</span>
                    <span className="text-[10px] font-mono-data px-2 py-0.5 rounded" style={{ background: stMeta.bg, color: stMeta.color }}>{stMeta.label}</span>
                  </DialogTitle>
                  <DialogDescription>
                    Ticket #{detail.id.slice(0, 8)} · <span className="font-mono-data">{days}d</span> en taller · SN {detail.serial_number || "—"}
                  </DialogDescription>
                </DialogHeader>

                <div className="space-y-4 mt-2">
                  <TicketIssueBlock
                    ticket={detail}
                    statusMeta={stMeta}
                    priorityMeta={prMeta}
                    editable={!["listo", "irreparable", "baja_definitiva"].includes(detail.status)}
                    onChanged={(fresh) => { setDetail(fresh); load(); }}
                  />

                  {/* Técnico sin asignar → selector (cuando ya hay técnico se muestra en el bloque principal) */}
                  {!detail.technician_name && (
                    <div className="border border-zinc-200 p-3 bg-white">
                      <Label className="text-xs uppercase tracking-wider">Técnico asignado</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Select value={detail.technician_id || ""} onValueChange={(v) => assignTech(detail.id, v)} disabled={!canWork}>
                          <SelectTrigger data-testid="assign-tech"><SelectValue placeholder="Asignar técnico..."/></SelectTrigger>
                          <SelectContent>
                            {employees.map((emp) => (
                              <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {canWork && !["listo", "irreparable", "baja_definitiva"].includes(detail.status) && (
                          <Button type="button" variant="outline" className="shrink-0 gap-1.5 border-[#002FA7]/40 text-[#002FA7] hover:bg-[#002FA7]/5" onClick={(e) => claimTicket(detail, e)} data-testid="detail-claim">
                            <ArrowRight size={13} weight="bold" /> Lo tomo yo
                          </Button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Cambio de estado desde el modal (misma dinámica que Hospital Interno) */}
                  {!["listo", "irreparable", "baja_definitiva"].includes(detail.status) && (
                    <div className="border border-zinc-200 p-3 bg-white flex items-center justify-between gap-3 flex-wrap">
                      <div>
                        <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500">Cambiar estado</div>
                        <div className="text-[11px] text-zinc-400 mt-0.5">Para dar de alta usa el botón {detail.source === "cliente" ? '"Entregar"' : '"Dar de alta"'}.</div>
                      </div>
                      <Select
                        value={detail.status}
                        onValueChange={async (v) => {
                          if (v === detail.status) return;
                          try {
                            const { data } = await api.patch(`/hospital/tickets/${detail.id}`, { status: v });
                            toast.success("Estado actualizado");
                            setDetail(data); load();
                          } catch (err) {
                            toast.error(err?.response?.data?.detail || "Error");
                          }
                        }}
                      >
                        <SelectTrigger className="w-64" data-testid="change-status">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {MODAL_STATE_OPTIONS.map((s) => {
                            const meta = STATUS_META[s];
                            return (
                              <SelectItem key={s} value={s} data-testid={`change-status-${s}`}>
                                <span className="inline-flex items-center gap-2">
                                  <span className="w-2 h-2 rounded-full" style={{ background: meta.color }} />
                                  {meta.label}
                                </span>
                              </SelectItem>
                            );
                          })}
                        </SelectContent>
                      </Select>
                    </div>
                  )}

                  {/* Bitácora del técnico (prioritario para el flujo diario) */}
                  <TicketNotesEditor ticket={detail} onSaved={(fresh) => { setDetail(fresh); load(); }} />

                  {/* Etiquetas visuales — compactas, secundarias */}
                  <TicketLabelsCompact
                    ticket={detail}
                    onChanged={(fresh) => {
                      if (fresh) setDetail(fresh);
                      load();
                    }}
                  />

                  {/* Datos del ticket — colapsado: ingreso, área, solicitó, ubicación, equipo del cliente */}
                  <Fold
                    title="Datos del ticket"
                    icon={Package}
                    testid="ticket-data-fold"
                    summary={[
                      `Ingresó ${new Date(detail.created_at).toLocaleDateString("es-MX")}`,
                      detail.requesting_area ? `Área ${detail.requesting_area}` : null,
                      detail.requested_by_name ? `Solicitó ${detail.requested_by_name}` : null,
                      detail.location ? `Ubic. ${detail.location}` : null,
                      detail.client_device ? `${detail.client_device.brand || ""} ${detail.client_device.model || ""}`.trim() : null,
                    ].filter(Boolean).join(" · ")}
                  >
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
                      <div><div className="text-zinc-500 uppercase font-mono-data">Ingreso</div><div className="font-mono-data">{new Date(detail.created_at).toLocaleDateString("es-MX")}</div></div>
                      <div><div className="text-zinc-500 uppercase font-mono-data">Área</div><div>{detail.requesting_area || "—"}</div></div>
                      <div><div className="text-zinc-500 uppercase font-mono-data">Solicitó</div><div>{detail.requested_by_name}</div></div>
                      <div><div className="text-zinc-500 uppercase font-mono-data">Ubicación</div><div>{detail.location || "—"}</div></div>
                    </div>
                    {detail.technician_name && detail.claimed_at && (
                      <div className="text-[11px] text-zinc-500">Técnico {detail.technician_name} tomó la tarjeta el {new Date(detail.claimed_at).toLocaleString("es-MX")}</div>
                    )}
                    {detail.client_device && (() => {
                      const cd = detail.client_device;
                      const hw = [cd.processor, cd.ram, cd.storage].filter(Boolean).join(" · ");
                      const extras = Object.entries(cd.specs || {}).filter(([k, v]) => v && k !== "includes_cable" && k !== "has_gpu");
                      return (
                        <div className="border border-[#E0000F]/20 bg-[#E0000F]/[0.03] p-3" data-testid="ticket-detail-device">
                          <div className="text-[10px] uppercase font-mono-data tracking-wider text-[#E0000F] font-semibold">Equipo del cliente · qué nos entregó</div>
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs mt-2">
                            <div><div className="text-zinc-500 uppercase font-mono-data text-[10px]">Equipo</div><div className="font-medium">{cd.brand} {cd.model}</div><div className="text-[10px] text-zinc-500">{cd.equipment_type}{cd.equipment_subtype ? ` · ${cd.equipment_subtype}` : ""}</div></div>
                            <div><div className="text-zinc-500 uppercase font-mono-data text-[10px]">Specs</div><div>{hw || "—"}</div></div>
                            <div><div className="text-zinc-500 uppercase font-mono-data text-[10px]">Cliente</div><div className="font-medium">{cd.customer_name || "—"}</div><div className="text-[10px] text-zinc-500 font-mono-data">{cd.customer_phone || ""}</div></div>
                            <div>
                              <div className="text-zinc-500 uppercase font-mono-data text-[10px] mb-1">Accesorios entregados</div>
                              <AccessoryChips device={cd} testid="ticket-detail-acc" />
                            </div>
                          </div>
                          {extras.length > 0 && (
                            <div className="text-[11px] text-zinc-600 mt-2">{extras.map(([k, v]) => `${k.replace(/_/g, " ")}: ${v}`).join(" · ")}</div>
                          )}
                        </div>
                      );
                    })()}
                  </Fold>

                  {/* Diagnóstico */}
                  {(detail.diagnosis || detail.actions_taken || canWork) && (
                    <div className="border border-zinc-200 p-3 bg-white space-y-2">
                      <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500 flex items-center gap-1.5">
                        <Wrench size={12} weight="bold"/> Diagnóstico y reparación
                      </div>
                      {detail.diagnosis && (
                        <div>
                          <div className="text-[10px] text-zinc-400 font-mono-data uppercase">Diagnóstico</div>
                          <div className="text-sm text-zinc-800">{detail.diagnosis}</div>
                        </div>
                      )}
                      {detail.actions_taken && (
                        <div>
                          <div className="text-[10px] text-zinc-400 font-mono-data uppercase">Acciones</div>
                          <div className="text-sm text-zinc-800 whitespace-pre-wrap">{detail.actions_taken}</div>
                        </div>
                      )}
                      {(detail.parts_used || detail.repair_cost > 0 || detail.minutes_invested > 0) && (
                        <div className="grid grid-cols-3 gap-2 text-xs pt-1 border-t border-zinc-100">
                          <div><span className="text-zinc-400 font-mono-data uppercase">Refacciones</span><div>{detail.parts_used || "—"}</div></div>
                          <div><span className="text-zinc-400 font-mono-data uppercase">Tiempo</span><div className="font-mono-data">{Math.round(detail.minutes_invested/60 * 10)/10}h</div></div>
                          <div><span className="text-zinc-400 font-mono-data uppercase">Costo</span><div className="font-mono-data text-[#E0000F] font-semibold">{fmtCurrency(detail.repair_cost)}</div></div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Historial */}
                  {detail.history && detail.history.length > 0 && (
                    <details className="border border-zinc-200 p-3 bg-white">
                      <summary className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500 cursor-pointer">Historial ({detail.history.length})</summary>
                      <ul className="mt-2 space-y-1 text-xs">
                        {detail.history.map((h, i) => (
                          <li key={i} className="flex items-start gap-2 text-zinc-600">
                            <span className="font-mono-data text-zinc-400 whitespace-nowrap">{new Date(h.at).toLocaleString("es-MX")}</span>
                            <span>· {h.by} ·</span>
                            <span className="font-semibold">{h.action}</span>
                            {h.note && <span className="text-zinc-500">— {h.note}</span>}
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>

                <DialogFooter className="mt-4 flex-wrap gap-2">
                  {detail.status === "pendiente_diagnostico" && (
                    <Button onClick={() => startDiagnosis(detail)} className="bg-amber-600 hover:bg-amber-700 text-white gap-1.5" data-testid="start-btn">
                      <Wrench size={14} weight="bold"/> Iniciar diagnóstico
                    </Button>
                  )}
                  {canWork && (
                    <>
                      <Button variant="outline" onClick={() => setCancelId(detail.id)} className="border-[#E0000F] text-[#E0000F] hover:bg-[#E0000F] hover:text-white gap-1.5" data-testid="cancel-btn">
                        <Trash size={14}/> Cancelar
                      </Button>
                      <Button onClick={() => openDischarge(detail)} className="bg-[#00873D] hover:bg-[#006b30] text-white gap-1.5" data-testid="discharge-btn">
                        <CheckCircle size={14} weight="fill"/> {detail.source === "cliente" ? "Entregar" : "Dar de alta"}
                      </Button>
                    </>
                  )}
                  <Button variant="outline" onClick={() => setDetail(null)}>Cerrar</Button>
                </DialogFooter>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>

      {/* DISCHARGE DIALOG */}
      <Dialog open={!!dischargeFor} onOpenChange={(o) => { if (!o) setDischargeFor(null); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl flex items-center gap-2">
              <CheckCircle size={20} weight="fill" className="text-[#00873D]"/>
              {dischargeFor?.source === "cliente" ? "Entregar al cliente" : "Dar de alta"}
            </DialogTitle>
            <DialogDescription>
              {dischargeFor?.source === "cliente"
                ? "Registra el diagnóstico final y cierra el ticket para entrega al cliente."
                : "Registra el diagnóstico final y decide si el equipo puede regresar a inventario."}
            </DialogDescription>
          </DialogHeader>
          {dischargeFor && (
            <form onSubmit={submitDischarge} className="space-y-3">
              <div className={`rounded-md border p-3 text-xs flex items-start gap-2 ${dischargeFor.source === "cliente" ? "border-[#E0000F]/25 bg-[#E0000F]/5 text-[#7a0000]" : "border-emerald-300 bg-emerald-50 text-emerald-800"}`}>
                {dischargeFor.source === "cliente" ? (
                  <>
                    <Warning size={14} weight="fill" className="mt-0.5"/>
                    <div>Este equipo es <strong>propiedad del cliente</strong>, no regresa a inventario. Al cerrar el ticket queda listo para su entrega.</div>
                  </>
                ) : (
                  <>
                    <CheckCircle size={14} weight="fill" className="mt-0.5"/>
                    <div>Al confirmar, el equipo pasa a <strong>Listo · alta</strong> y vuelve a inventario disponible. Escribe una <strong>justificación</strong> del cierre.</div>
                  </>
                )}
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider">
                  {dischargeFor.source === "cliente" ? "Justificación / diagnóstico *" : "Justificación del cierre *"}
                </Label>
                <Textarea
                  rows={3}
                  required
                  value={dischargeForm.diagnosis}
                  onChange={(e) => setDischargeForm({ ...dischargeForm, diagnosis: e.target.value })}
                  placeholder="Ej: Se reemplazó módulo RAM defectuoso, equipo prueba estable 30 min."
                  className="mt-1"
                  data-testid="d-diagnosis"
                  autoFocus
                />
                <div className="text-[10px] text-zinc-400 mt-1">Este texto queda en la bitácora como razón del cierre.</div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider">Acciones realizadas</Label>
                <Textarea rows={2} value={dischargeForm.actions_taken} onChange={(e) => setDischargeForm({ ...dischargeForm, actions_taken: e.target.value })} placeholder="Detalle técnico opcional…" className="mt-1" data-testid="d-actions"/>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-xs uppercase tracking-wider">Tiempo (min)</Label>
                  <Input type="number" min="0" value={dischargeForm.minutes_invested} onChange={(e) => setDischargeForm({ ...dischargeForm, minutes_invested: parseInt(e.target.value, 10) || 0 })} className="mt-1 numeric" data-testid="d-minutes"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Costo</Label>
                  <Input type="number" step="0.01" min="0" value={dischargeForm.repair_cost} onChange={(e) => setDischargeForm({ ...dischargeForm, repair_cost: parseFloat(e.target.value) || 0 })} className="mt-1 numeric" data-testid="d-cost"/>
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider">Refacciones</Label>
                  <Input value={dischargeForm.parts_used} onChange={(e) => setDischargeForm({ ...dischargeForm, parts_used: e.target.value })} placeholder="RAM 8GB, HDD…" className="mt-1" data-testid="d-parts"/>
                </div>
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDischargeFor(null)}>Cancelar</Button>
                <Button type="submit" disabled={saving} className="bg-[#00873D] hover:bg-[#006b30] text-white" data-testid="d-submit">
                  {saving ? "Procesando..." : (
                    dischargeFor?.source === "cliente" ? "Entregar al cliente" : "Confirmar alta · Listo"
                  )}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* CANCEL TICKET */}
      <AlertDialog open={!!cancelId} onOpenChange={(o) => { if (!o) setCancelId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Cancelar este ticket?</AlertDialogTitle>
            <AlertDialogDescription>La unidad se libera automáticamente y vuelve a estar disponible para venta.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>No</AlertDialogCancel>
            <AlertDialogAction onClick={confirmCancel} className="bg-[#E0000F] hover:bg-[#b8000c] text-white" data-testid="cancel-confirm">Sí, cancelar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

// ─────────────────────── Ticket notes editor ──────────────────────────────
function TicketNotesEditor({ ticket, onSaved }) {
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  // Últimas 2 notas del historial (kind === "note") — se muestran al frente.
  const notes = (ticket?.history || [])
    .filter((h) => h.action === "note")
    .slice(-2)
    .reverse();

  const submit = async () => {
    const text = note.trim();
    if (!text) { toast.error("Escribe una nota"); return; }
    setSaving(true);
    try {
      const { data } = await api.post(`/hospital/tickets/${ticket.id}/notes`, { note: text });
      toast.success("Nota registrada");
      setNote("");
      onSaved?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo registrar la nota");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-zinc-200 p-3 bg-white space-y-2" data-testid="ticket-notes-editor">
      <div className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-500 flex items-center gap-1.5">
        <ClipboardText size={12} weight="bold" /> Bitácora del técnico
      </div>
      <p className="text-[11px] text-zinc-500 -mt-1">
        Documenta lo que hiciste, dónde te quedaste y qué falta. Los siguientes técnicos podrán continuar donde lo dejaste.
      </p>
      {notes.length > 0 && (
        <div className="space-y-2">
          {notes.map((h, i) => (
            <div key={i} className="border-l-2 border-[#002FA7] bg-[#002FA7]/5 p-2 rounded-sm text-xs">
              <div className="flex items-baseline justify-between gap-2 flex-wrap">
                <span className="font-semibold text-zinc-900">{h.by_name || h.by}</span>
                <span className="numeric text-zinc-400 whitespace-nowrap text-[10px]">{new Date(h.at).toLocaleString("es-MX")}</span>
              </div>
              <p className="text-zinc-700 whitespace-pre-line mt-0.5">{h.note}</p>
            </div>
          ))}
        </div>
      )}
      <div className="flex gap-2 items-end">
        <textarea
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={3}
          autoFocus
          placeholder="Ej: Cambié la pantalla, falta pegar el marco y probar la webcam."
          className="flex-1 text-sm border border-zinc-200 rounded-md p-2 focus:outline-none focus:border-[#002FA7] resize-none"
          data-testid="ticket-note-input"
        />
        <Button onClick={submit} disabled={!note.trim() || saving} className="gap-1 bg-[#002FA7] hover:bg-[#0043c2] shrink-0" data-testid="ticket-note-submit">
          Registrar
        </Button>
      </div>
    </div>
  );
}



// ─────────────────────── Labels (etiquetas) ─────────────────────────────────
const TICKET_LABEL_PALETTE = [
  "#E0000F", "#F97316", "#EAB308", "#22C55E", "#0891B2",
  "#002FA7", "#7C3AED", "#DB2777", "#52525B", "#000000",
];

function TicketLabelsEditor({ ticket, onChanged }) {
  const [labels, setLabels] = useState(ticket?.labels || []);
  const [draft, setDraft] = useState({ text: "", color: TICKET_LABEL_PALETTE[0] });
  const [saving, setSaving] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [manageOpen, setManageOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  useEffect(() => { setLabels(ticket?.labels || []); }, [ticket?.id, ticket?.labels]);
  const reloadCatalog = () =>
    api.get("/hospital/tickets/labels/catalog")
      .then((r) => setCatalog(r.data || []))
      .catch(() => setCatalog([]));
  useEffect(() => { reloadCatalog(); }, [ticket?.id]);

  const persist = async (next) => {
    setSaving(true);
    try {
      const { data } = await api.put(`/hospital/tickets/${ticket.id}/labels`, { labels: next });
      setLabels(data.labels || next);
      onChanged?.(data);
      reloadCatalog();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo guardar la etiqueta");
    } finally {
      setSaving(false);
    }
  };

  const addLabel = () => {
    const text = draft.text.trim();
    if (!text) { toast.error("Escribe un texto"); return; }
    if (labels.some((l) => l.text === text && (l.color || "") === (draft.color || ""))) {
      toast.info("Ya tiene esa etiqueta"); return;
    }
    persist([...labels, { text, color: draft.color }]);
    setDraft({ text: "", color: draft.color });
  };
  const removeLabel = (idx) => persist(labels.filter((_, i) => i !== idx));

  const saveEdit = async () => {
    const text = (editing.to_text || "").trim();
    if (!text) { toast.error("El texto no puede estar vacío"); return; }
    try {
      await api.patch("/hospital/tickets/labels/catalog", {
        from_text: editing.from_text,
        from_color: editing.from_color,
        to_text: text,
        to_color: editing.to_color,
      });
      toast.success("Etiqueta actualizada en todas las tarjetas");
      setEditing(null);
      reloadCatalog();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo actualizar");
    }
  };

  const deleteFromCatalog = async (c) => {
    if (!window.confirm(`Eliminar "${c.text}" de todas las tarjetas donde aparece (${c.count})?`)) return;
    try {
      await api.post("/hospital/tickets/labels/catalog/delete", { text: c.text, color: c.color });
      toast.success("Etiqueta eliminada del catálogo");
      reloadCatalog();
      onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo eliminar");
    }
  };

  const availableFromCatalog = catalog.filter(
    (c) => !labels.some((l) => l.text === c.text && (l.color || "") === (c.color || ""))
  );

  return (
    <section data-testid="ticket-labels-editor">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Etiquetas</div>
        {catalog.length > 0 && (
          <button
            type="button"
            onClick={() => setManageOpen((v) => !v)}
            className="text-[10px] uppercase tracking-widest text-zinc-500 hover:text-[#002FA7] inline-flex items-center gap-1"
            data-testid="ticket-manage-catalog-toggle"
          >
            <PencilSimple size={11} weight="bold" />
            {manageOpen ? "Cerrar" : "Gestionar catálogo"}
          </button>
        )}
      </div>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {labels.length === 0 && (
          <span className="text-xs text-zinc-400 italic">Sin etiquetas. Añade una para identificar rápido este equipo.</span>
        )}
        {labels.map((lb, i) => (
          <span
            key={i}
            className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm text-white"
            style={{ background: lb.color || "#52525B" }}
            data-testid={`ticket-editor-label-${i}`}
          >
            {lb.text}
            <button
              type="button"
              onClick={() => removeLabel(i)}
              disabled={saving}
              className="hover:bg-black/20 rounded-full p-0.5 -mr-1"
              aria-label={`Quitar etiqueta ${lb.text}`}
              data-testid={`ticket-editor-label-rm-${i}`}
            >
              <X size={9} weight="bold" />
            </button>
          </span>
        ))}
      </div>

      {manageOpen && (
        <div className="border border-zinc-200 rounded-md p-3 mb-3 bg-zinc-50/50" data-testid="ticket-manage-catalog-panel">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold mb-2">
            Catálogo global — editar o eliminar afecta todas las tarjetas
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {catalog.map((c, i) => {
              const key = `${c.text}::${c.color}`;
              const isEditing = editing && editing.from_text === c.text && editing.from_color === c.color;
              if (isEditing) {
                return (
                  <div key={key} className="flex flex-wrap items-center gap-1.5 p-2 rounded-sm bg-white border border-[#002FA7]/40">
                    <Input
                      value={editing.to_text}
                      onChange={(e) => setEditing({ ...editing, to_text: e.target.value })}
                      className="h-7 max-w-[140px] text-xs"
                      data-testid={`ticket-edit-label-text-${i}`}
                    />
                    <div className="flex gap-0.5">
                      {TICKET_LABEL_PALETTE.map((color) => (
                        <button
                          key={color}
                          type="button"
                          onClick={() => setEditing({ ...editing, to_color: color })}
                          className={`w-5 h-5 rounded-sm border transition-all ${editing.to_color === color ? "border-zinc-900 scale-110" : "border-transparent"}`}
                          style={{ background: color }}
                          data-testid={`ticket-edit-label-color-${color}`}
                        />
                      ))}
                    </div>
                    <Button size="sm" onClick={saveEdit} className="h-7 bg-[#002FA7] hover:bg-[#0043c2] text-xs">
                      Guardar
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setEditing(null)} className="h-7 text-xs">
                      Cancelar
                    </Button>
                  </div>
                );
              }
              return (
                <div key={key} className="flex items-center justify-between gap-2 p-1.5 rounded-sm hover:bg-white transition-colors group">
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm text-white truncate"
                      style={{ background: c.color || "#52525B" }}
                    >
                      {c.text}
                    </span>
                    <span className="text-[10px] text-zinc-400 font-mono-data">{c.count} tarjeta{c.count !== 1 ? "s" : ""}</span>
                  </div>
                  <div className="flex items-center gap-1 opacity-60 group-hover:opacity-100 transition-opacity">
                    <button
                      type="button"
                      onClick={() => setEditing({ from_text: c.text, from_color: c.color, to_text: c.text, to_color: c.color })}
                      className="text-zinc-500 hover:text-[#002FA7] p-1"
                      aria-label="Editar etiqueta"
                    >
                      <PencilSimple size={12} weight="bold" />
                    </button>
                    <button
                      type="button"
                      onClick={() => deleteFromCatalog(c)}
                      className="text-zinc-500 hover:text-[#E0000F] p-1"
                      aria-label="Eliminar etiqueta"
                    >
                      <Trash size={12} weight="bold" />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {availableFromCatalog.length > 0 && !manageOpen && (
        <div className="mb-2">
          <div className="text-[10px] text-zinc-500 mb-1">Usadas en otras tarjetas — click para agregar:</div>
          <div className="flex flex-wrap gap-1.5">
            {availableFromCatalog.map((c, i) => (
              <button
                type="button"
                key={i}
                onClick={() => persist([...labels, { text: c.text, color: c.color }])}
                disabled={saving}
                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm text-white opacity-70 hover:opacity-100 transition-opacity"
                style={{ background: c.color || "#52525B" }}
                data-testid={`ticket-catalog-label-${i}`}
              >
                <Plus size={9} weight="bold" /> {c.text}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 items-center">
        <Input
          value={draft.text}
          onChange={(e) => setDraft({ ...draft, text: e.target.value })}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addLabel(); } }}
          placeholder="Nueva etiqueta…"
          className="h-8 max-w-xs"
          data-testid="ticket-label-input"
        />
        <div className="flex gap-1">
          {TICKET_LABEL_PALETTE.map((color) => (
            <button
              type="button"
              key={color}
              onClick={() => setDraft({ ...draft, color })}
              className={`w-6 h-6 rounded-sm border-2 transition-all ${draft.color === color ? "border-zinc-900 scale-110" : "border-transparent hover:border-zinc-300"}`}
              style={{ background: color }}
              aria-label={`Color ${color}`}
              data-testid={`ticket-label-color-${color}`}
            />
          ))}
        </div>
        <Button onClick={addLabel} disabled={!draft.text.trim() || saving} size="sm" className="gap-1 bg-[#002FA7] hover:bg-[#0043c2]" data-testid="ticket-label-add">
          <Plus size={12} weight="bold" /> Crear
        </Button>
      </div>
    </section>
  );
}

// ─────────────────────── Etiquetas compactas (modo secundario) ─────────────
// Versión reducida del editor: pills en línea + un botón "+" que abre un
// popover con toda la funcionalidad (paleta, crear, catálogo, gestionar).
// Diseñada para que el técnico se enfoque en la Bitácora — las etiquetas
// no roban foco ni espacio visual.
function TicketLabelsCompact({ ticket, onChanged }) {
  const [open, setOpen] = useState(false);
  const labels = ticket?.labels || [];

  const removeLabel = async (idx) => {
    const next = labels.filter((_, i) => i !== idx);
    try {
      const { data } = await api.put(`/hospital/tickets/${ticket.id}/labels`, { labels: next });
      onChanged?.(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo quitar");
    }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap px-1" data-testid="ticket-labels-compact">
      <span className="text-[10px] uppercase font-mono-data tracking-wider text-zinc-400">Etiquetas</span>
      {labels.length === 0 && (
        <span className="text-[11px] text-zinc-400 italic">Ninguna</span>
      )}
      {labels.map((lb, i) => (
        <span
          key={i}
          className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-sm text-white"
          style={{ background: lb.color || "#52525B" }}
        >
          {lb.text}
          <button
            type="button"
            onClick={() => removeLabel(i)}
            className="hover:bg-black/20 rounded-full p-0.5 -mr-1"
            aria-label={`Quitar ${lb.text}`}
            data-testid={`ticket-compact-rm-${i}`}
          >
            <X size={9} weight="bold" />
          </button>
        </span>
      ))}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center justify-center w-5 h-5 rounded-sm border border-dashed border-zinc-300 text-zinc-400 hover:border-[#002FA7] hover:text-[#002FA7] transition-colors"
            data-testid="ticket-labels-compact-add"
            title="Agregar / gestionar etiquetas"
          >
            <Plus size={10} weight="bold" />
          </button>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[420px] p-3 max-h-[70vh] overflow-y-auto">
          <TicketLabelsEditor
            ticket={ticket}
            onChanged={(fresh) => {
              if (fresh) onChanged?.(fresh);
            }}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

